import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { get } from "lodash-es"
import moment from "moment"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { ReportDataset } from "shared/report-datasets"
import { useAppSelector } from "src/app/hooks"
import {
  getDateFormat,
  isValidReportDate,
  REPORT_MAX_DATE,
  REPORT_MIN_DATE,
  truncateDate,
} from "src/charts/chartsCommon"
import { DefaultColorMap } from "src/charts/shared"
import { selectTenantId } from "src/features/me/meSlice"
import { useRequestReportDataMutation } from "src/features/report/reportApi"
import { Sl2Btn } from "./Sl2Btn"

export type OsReportReservationSummaryPickupProps = {}
export const OsReportReservationSummaryPickup: FC<
  OsReportReservationSummaryPickupProps
> = () => {
  const { t } = useTranslation()

  const tenantId = useAppSelector(selectTenantId)

  const [requestReportData, { isLoading, isError }] =
    useRequestReportDataMutation()

  const [ds, setDs] = useState<ReportDataset<any> | null>(null)

  const [period, setPeriod] = useState("day")
  // last 1month
  const [startDate, setStartDate] = useState(
    moment(moment().subtract(1, "month")),
  )
  const [endDate, setEndDate] = useState(moment())

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const [validationError, setValidationError] = useState<string | null>(null)

  const handleStartDateChange = useCallback((date: moment.Moment | null) => {
    if (isValidReportDate(date)) setStartDate(date)
  }, [])

  const handleEndDateChange = useCallback((date: moment.Moment | null) => {
    if (isValidReportDate(date)) setEndDate(date)
  }, [])

  const refresh = useCallback(async () => {
    if (!tenantId) {
      return
    }
    try {
      setValidationError(null)
      if (startDate.isAfter(endDate)) {
        setSnackMessage({
          message: t("reporting.dateRangeError"),
          severity: "error",
        })
        setValidationError(t("reporting.dateRangeError"))
        return
      }
      const res = await requestReportData({
        body: {
          reportType: "ReservationSummaryPickup",
          tenantId,
          input: {
            tenantId,

            startDate: truncateDate(startDate, period).format(
              getDateFormat(period),
            ),
            endDate: truncateDate(endDate, period).format(
              getDateFormat(period),
            ),
          },
        },
      }).unwrap()
      const datasets = get(res, "res.datasets", {}) as any
      if (datasets?.reservationPickupHod) {
        setDs(datasets.reservationPickupHod)
      }
    } catch (e) {
      console.error("Error fetching report data", e)
      setSnackMessage({
        message: t("reporting.fetchError"),
        severity: "error",
      })
    }
  }, [endDate, period, requestReportData, startDate, tenantId, t])

  useEffect(
    () => {
      refresh()
    } /* eslint-disable react-hooks/exhaustive-deps */,
    [tenantId],
  )

  const getMaxTotalReservations = (data: any[]) => {
    return Math.max(...data.map((d) => d.total_reservations))
  }

  return (
    <Stack direction="column" spacing={4} p={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={0}>
          <Typography variant="body1" fontWeight={"bold"}>
            {t("reporting.reservationSummaryPickupHod.title")}
          </Typography>
          <Typography variant="body2">
            {t("reporting.reservationSummaryPickupHod.description")}
          </Typography>

          {/* filter controls */}
          <Stack direction="row" spacing={2} alignItems="center" pt={4}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={t("reporting.startDate")}
                value={startDate}
                onChange={handleStartDateChange}
                minDate={REPORT_MIN_DATE}
                maxDate={REPORT_MAX_DATE}
                slotProps={{ textField: { size: "small" } }}
                // format="YYYY/MM/DD hh A"
              />
              <DatePicker
                label={t("reporting.endDate")}
                value={endDate}
                onChange={handleEndDateChange}
                minDate={REPORT_MIN_DATE}
                maxDate={REPORT_MAX_DATE}
                slotProps={{ textField: { size: "small" } }}
                // format="YYYY/MM/DD hh A"
              />
            </LocalizationProvider>

            <Sl2Btn
              onClick={refresh}
              disabled={isLoading}
              sx={{
                minWidth: "100px",
              }}
              variant="contained"
              color="primary"
            >
              {t("apply")}
            </Sl2Btn>
            <Typography variant="caption">
              {ds?.lastUpdated &&
                `${t("lastUpdated")}: ${moment(ds.lastUpdated).format(
                  "YYYY-MM-DD HH:mm:ss",
                )}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Box>
        {isError && <Alert severity="error">{t("reporting.fetchError")}</Alert>}
        {validationError && <Alert severity="error">{validationError}</Alert>}
      </Box>
      <Box
        sx={{
          height: 350,
        }}
      >
        {ds && ds?.data?.length > 0 && !isLoading ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ds.data} margin={{ bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour_of_day" padding={{ right: 20 }}>
                <Label
                  value={t("reporting.reservationSummaryPickupHod.xAxisLabel")}
                  offset={-10}
                  position="insideBottom"
                />
              </XAxis>
              <YAxis
                domain={[0, getMaxTotalReservations(ds.data)]}
                padding={{ top: 20 }}
                label={{
                  value: t("reporting.reservationSummaryPickupHod.yAxisLabel"),
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle" },
                }}
              />
              {/* <Tooltip labelFormatter={(value) => `Hour of day ${value}h`} /> */}
              <Tooltip
                labelFormatter={(hour) =>
                  t("reporting.reservationSummaryPickupHod.tooltip", {
                    hour,
                  })
                }
              />
              <Line
                key={"pickup_count"}
                type="monotone"
                dataKey={`pickup_count`}
                stroke={DefaultColorMap[1]}
                activeDot={{ r: 8 }}
                name={`${t(
                  "reporting.reservationSummaryPickupHod.yAxisLabel",
                )}`}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              //grey box
              width: "100%",
              height: 300,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "grey.100",
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Typography>{t("noData")}</Typography>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snackMessage.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
    </Stack>
  )
}

export default OsReportReservationSummaryPickup
